/*!
 * collection-tabs.js — AJAX-swap tab navigation for collection PDPs with tabs.
 *
 * Companion JS to sections/collection-tabs-nav.liquid.
 *
 * Architecture
 * ------------
 * Tabs are server-rendered as plain <a href> anchors. This script is a
 * progressive enhancement: with JS disabled, clicks navigate normally
 * (full page load). With JS enabled, clicks on [data-tab-ajax="true"]:
 *   1. Fetch the target URL + ?section_id=main (Shopify Section Rendering API)
 *   2. Replace the existing #shopify-section-main element with the response
 *      (only when the response is that same wrapper AND holds a collection
 *      grid; otherwise do a real navigation)
 *   3. Push the new URL into history (URL bar updates; not on back/forward)
 *   4. Re-bind Stiletto's collection section on the new grid (filter chain,
 *      infinite scroll, quick view, ProductItem) via Shopify.theme.sections,
 *      then dispatch shopify:section:load for everyone else
 *   5. Wrap the swap in document.startViewTransition() when available
 *      so the browser does an automatic crossfade (Chrome 111+/Safari 18+)
 *
 * Pages without a collection grid in __main (e.g. the BFCM Collectibles
 * page, which shares the hub's tab strip) are left alone: tabs are links.
 *
 * Header + tab strip never re-paint — they remain in the DOM. Only the
 * product grid section swaps.
 *
 * Pre-fetch
 * ---------
 * On `mouseenter` (desktop) or `touchstart` (mobile) of a non-active
 * AJAX tab, fire the fetch immediately and cache the response. Most
 * users commit within 200-500ms of hover, so the round-trip completes
 * before the click registers → swap feels instantaneous.
 *
 * Analytics
 * ---------
 * Dual-pipe: window.dataLayer (Elevar/GTM/Klaviyo) + Shopify.analytics.publish
 * (Shopify Analytics + connected pixels). Same pattern as the mobile-nav +
 * search-modal instrumentation (commit f60d4b6).
 *
 * Events:
 *   - collection_tab_view      : on initial page load (which tab is active)
 *   - collection_tab_switch    : on every swap (from/to/method/dwell/prefetched)
 *   - collection_tab_prefetch  : on speculative hover-fetch
 *
 * Browser support
 * ---------------
 *   - fetch + Promise           : ES6 baseline, all evergreen browsers
 *   - history.pushState         : 95%+ coverage
 *   - document.startViewTransition : Chrome 111+, Safari 18+, no Firefox yet.
 *     Feature-detected; falls back to direct swap on older browsers.
 */
(function () {
  'use strict';

  var nav = document.querySelector('[data-collection-tabs]');
  if (!nav) return;

  // Discover the swap target's actual ID from the DOM. Shopify wraps
  // sections rendered from a TEMPLATE JSON file in
  //   <div id="shopify-section-template--{templateInstanceId}__{sectionKey}">
  // NOT just `shopify-section-{key}` (which is the convention for STATIC
  // sections mounted in layouts). The template_id segment is the same for
  // any collection sharing the template (e.g. both -rtw and -couture
  // use collection.with-tabs → same template_id), so the section_id we
  // discover here also works in the fetch URL targeting sister collections.
  //
  // The corresponding ?section_id= parameter strips the `shopify-section-`
  // prefix: `?section_id=template--{templateInstanceId}__{sectionKey}`.
  // The bare `?section_id=main` returns 404 for templated sections.
  var swapWrapper = document.querySelector('[id^="shopify-section-"][id$="__main"]')
                 || document.getElementById('shopify-section-main');
  if (!swapWrapper) {
    // No recognisable swap target — JS noops, tabs fall back to plain anchor navigation
    return;
  }
  // Only a page that already shows a collection grid can swap grids in place.
  // On a page template (e.g. page.bfcm-collectibles) __main is another section
  // type, so the tabs stay plain links: no prefetch, no interception, a real
  // navigation. (The Section Rendering API would happily render the page's
  // section for a collection URL, so the swap must not even be attempted.)
  var GRID_SEL = '[data-section-type="collection"]';
  if (!swapWrapper.querySelector(GRID_SEL)) return;
  var SECTION_TARGET_ID = swapWrapper.id;
  var SECTION_FETCH_ID  = SECTION_TARGET_ID.replace(/^shopify-section-/, '');

  // Which collection's grid is in the DOM right now. Back/forward decide
  // whether to swap by comparing the URL with this, never with the active
  // tab (other scripts may flip the tab before we run).
  var gridPath = window.location.pathname;
  var swapSeq = 0;                 // newest swap wins if two are in flight
  var skeletonTimer = null;
  function endSkeleton() {
    clearTimeout(skeletonTimer);
    nav.removeAttribute('data-collection-tabs-swapping');
  }

  var prefetchCache = {};          // href -> Promise<string>
  var prefetchHits = {};           // href -> bool (did we serve from cache?)
  var currentTabActivatedAt = Date.now();  // for tab_dwell_ms

  function pathOf(href) { return String(href || '').split('#')[0].split('?')[0]; }

  // Read by sections/bfcm-hub-looks.liquid so its popstate handler can leave
  // cross-collection steps to us.
  window.CollectionTabs = { gridPath: function () { return gridPath; } };

  // ============================================================
  // Analytics — same dual-pipe shape as nav + search instrumentation.
  // Defined as a local helper (no shared global) for the same reason
  // as the other sections: keeps each section's analytics self-contained
  // and avoids another inline blocking script in <head>.
  // ============================================================
  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (_) { /* never let tracking break navigation */ }
  }

  // ============================================================
  // Tab metadata extraction — each tab anchor carries data-* attributes
  // from the Liquid render. Centralised so tests can mock easily.
  // ============================================================
  function tabMeta(tab) {
    return {
      label: tab.getAttribute('data-tab-label') || tab.textContent.trim().slice(0, 80),
      type: tab.getAttribute('data-tab-type') || 'unknown',
      target: tab.getAttribute('data-tab-target') || '',
      href: tab.getAttribute('href') || '',
      isAjax: tab.getAttribute('data-tab-ajax') === 'true',
      isActive: tab.classList.contains('collection-view-switcher__tab--active')
    };
  }

  // ============================================================
  // Pre-fetch — fires on hover/touchstart for non-active AJAX tabs.
  // Response is cached so the click handler can read it instantly.
  // ============================================================
  function prefetch(href) {
    href = String(href).split('#')[0];
    if (prefetchCache[href]) return prefetchCache[href];
    var url = href + (href.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(SECTION_FETCH_ID);
    prefetchCache[href] = fetch(url, {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .catch(function () { delete prefetchCache[href]; return ''; });
    return prefetchCache[href];
  }

  // ============================================================
  // Pre-decode above-the-fold images BEFORE the visible swap.
  // ============================================================
  // User-reported issue (iPhone Safari incognito): "screen goes white
  // first then updates". Root cause is that View Transitions takes the
  // NEW state snapshot synchronously after the DOM swap, but the new
  // section's product images haven't downloaded yet — so the snapshot
  // is mostly empty/white. The browser then crossfades from OLD
  // (with images visible) → NEW (white placeholders) → images pop in
  // after the transition completes. The white gap is what the user sees.
  //
  // Fix: parse the response HTML, pre-fetch the first N images via
  // the Image() constructor (which respects the browser HTTP cache).
  // Once those images are in cache, the visible swap proceeds — both
  // the View Transitions snapshot AND the fade-fallback path now see
  // the new section with images fully loaded.
  //
  // Capped at 8 images so we don't block on the entire infinite-scroll
  // dataset. Capped at 600ms via Promise.race so a slow image can't
  // freeze the UX indefinitely.
  function preloadAboveFoldImages(scope) {
    var imgs = Array.prototype.slice.call(scope.querySelectorAll('img'), 0, 8);
    if (imgs.length === 0) return Promise.resolve();
    var loads = imgs.map(function (img) {
      // currentSrc isn't populated until the img is in the DOM, so fall
      // back to src / data-src for offline-parsed nodes.
      var src = img.getAttribute('src') || img.getAttribute('data-src') || '';
      // Strip srcset width sizing — we just want the highest available
      var srcset = img.getAttribute('srcset') || '';
      if (srcset) {
        var first = srcset.split(',')[0].trim().split(' ')[0];
        if (first) src = first;
      }
      if (!src) return Promise.resolve();
      return new Promise(function (resolve) {
        var preloadImg = new Image();
        preloadImg.onload = function () { resolve(); };
        preloadImg.onerror = function () { resolve(); }; // don't block on errors
        preloadImg.src = src;
      });
    });
    var allLoaded = Promise.all(loads);
    var timeout = new Promise(function (resolve) { setTimeout(resolve, 600); });
    return Promise.race([allLoaded, timeout]);
  }

  // ============================================================
  // Swap logic — replaces #shopify-section-main with the response,
  // updates URL, re-binds Stiletto sections, wraps in View Transitions.
  // ============================================================
  // Stiletto only re-initialises sections on shopify:section:load inside the
  // theme editor (theme.js `if (window.Shopify.designMode)`). On the storefront
  // the swapped-in grid would get no collection instance: infinite scroll,
  // Filter & Sort and quick view would stay bound to the detached old grid.
  // So retire the old instance and create one for the new grid ourselves.
  function rebindTheme(oldC, newC) {
    var S = window.Shopify && Shopify.theme && Shopify.theme.sections;
    if (!S || !S.instances || !S.registered) return;
    for (var i = S.instances.length - 1; i >= 0; i--) {
      var inst = S.instances[i];
      var c = inst && inst.container;
      if (!c || (c !== oldC && document.documentElement.contains(c))) continue;
      S.instances.splice(i, 1);
      try { inst.onUnload && inst.onUnload(); } catch (_) {}
      // A filter popstate render the old instance started just before the
      // swap can still resolve later. Ajaxinate looks its elements up with
      // document.querySelector, so a late _initInfiniteScroll() would bind a
      // second infinite scroll to the NEW grid (duplicate pages). Defuse it.
      inst.infiniteScrollTrigger = null;
      inst._initInfiniteScroll = function () {};
      inst.filterDrawer = inst.filterBar = inst.animateCollection = null;
    }
    // In the theme editor Stiletto's own section:load listener creates it.
    if (window.Shopify && Shopify.designMode) return;
    var T = newC && S.registered[newC.getAttribute('data-section-type')];
    if (T && !S.instances.some(function (x) { return x.container === newC; })) {
      try { S.instances.push(new T(newC)); } catch (_) {}
    }
  }

  // newTab: the tab to mark active. url: what to fetch (the tab's href on a
  // click; the popped URL on back/forward, so filters in it are kept).
  // fromPop: the URL is already in the address bar, so no pushState.
  function doSwap(newTab, url, via, fromPop) {
    var prevTab = nav.querySelector('.collection-view-switcher__tab--active');
    var prevTabMeta = prevTab ? tabMeta(prevTab) : null;
    if (!newTab) return false;
    var href = String(url).split('#')[0];
    var label = tabMeta(newTab).label;
    var mySeq = ++swapSeq;
    var newTabMeta = tabMeta(newTab);
    var wasPrefetched = !!prefetchCache[href];
    var dwellMs = Date.now() - currentTabActivatedAt;
    function fullNav() {
      // The hub link's first-tab swap on load never reloads (it would loop): it goes to the tab.
      if (via === 'auto') window.location.replace(href);
      else if (fromPop) window.location.reload();
      else window.location.href = href;
    }

    // Show skeleton if the swap takes >150ms (slow network only).
    // NOTE: optimistic active-tab flip moved INTO performSwap so it
    // animates together with the product grid (avoids the underline
    // jumping ahead of the grid crossfade and reading as flicker).
    // One shared timer: a newer swap owns the skeleton state.
    clearTimeout(skeletonTimer);
    skeletonTimer = setTimeout(function () {
      nav.setAttribute('data-collection-tabs-swapping', 'true');
    }, 150);

    var fetchPromise = prefetch(href);

    fetchPromise.then(function (html) {
      if (mySeq !== swapSeq) return; // a newer tab tap / history step superseded this one
      endSkeleton();

      if (!html) {
        // Fetch failed — fall through to full nav
        fullNav();
        return;
      }

      // Extract the new wrapper from the response. Section Rendering
      // API returns just the section's HTML wrapped in
      //   <div id="shopify-section-{sectionId}" class="shopify-section ...">
      // The id matches what we captured at init time (SECTION_TARGET_ID)
      // because both source and target collections share the same template.
      // Swap only when it is that same wrapper AND it holds a collection
      // grid; anything else is a different template → real navigation.
      var temp = document.createElement('div');
      temp.innerHTML = html;
      var newSection = temp.querySelector('#' + CSS.escape(SECTION_TARGET_ID));
      var oldSection = document.getElementById(SECTION_TARGET_ID);
      if (!newSection || !oldSection || !newSection.querySelector(GRID_SEL)) {
        // Structure mismatch — bail to full nav so UX still works
        fullNav();
        return;
      }

      function performSwap() {
        if (mySeq !== swapSeq) return;
        // Active-state flip happens INSIDE the View Transitions
        // callback (or inside the fade-fallback timing on browsers
        // without VT). Browsers with VT take snapshots at this
        // boundary, so the underline crossfades with the grid.
        // Without this co-location, the underline would jump first
        // (instant CSS state change) and the products would
        // crossfade ~180ms later — perceived as a 2-step flicker.
        nav.querySelectorAll('.collection-view-switcher__tab--active').forEach(function (t) {
          t.classList.remove('collection-view-switcher__tab--active');
          t.setAttribute('aria-selected', 'false');
          t.removeAttribute('aria-current');
        });
        newTab.classList.add('collection-view-switcher__tab--active');
        newTab.setAttribute('aria-selected', 'true');
        newTab.setAttribute('aria-current', 'page');

        // Per-tab layout override (e.g. low-density grid on Couture).
        // The new tab's layout intent is read from its data attributes
        // (server-rendered by the Liquid). Updating data-layout on the
        // nav root takes effect immediately via the CSS sibling selector
        // targeting the product-grid section.
        var newLayout = newTab.getAttribute('data-tab-layout') || 'standard';
        var newEditorial = newTab.getAttribute('data-tab-editorial') || 'false';
        nav.setAttribute('data-layout', newLayout);
        nav.setAttribute('data-editorial', newEditorial);

        // Track the new active block so SSR-derived state stays in sync
        var newActiveId = newTab.getAttribute('data-block-id');
        if (newActiveId) nav.setAttribute('data-active-block', newActiveId);

        var oldGrid = oldSection.querySelector(GRID_SEL);
        oldSection.replaceWith(newSection);
        document.documentElement.classList.remove('bfcm-autotab');
        gridPath = pathOf(href);
        // Update URL — preserve scroll position via the third arg form.
        // Back/forward already moved the URL: pushing again would add a
        // duplicate entry and wipe the forward stack.
        if (!fromPop) history.pushState({ collectionTabSwap: true, href: href, label: label }, '', href);
        // The hub link's first-tab swap: the address becomes the tab's own URL (replaced, no new
        // entry). Filter & Sort builds its requests from the address, so it now filters the tab's
        // collection instead of the hub's (whose products are drafts: an empty reply stalled the grid).
        else if (via === 'auto') history.replaceState({ collectionTabSwap: true, href: href, label: label }, '', href + location.hash);
        // Name the entry after the collection now on screen (tabs that carry it: the BFCM hub).
        // After pushState, so the entry we left keeps its own title in the history menu.
        var docTitle = newTab.getAttribute('data-tab-doc-title');
        if (docTitle) document.title = docTitle;
        // Give the new grid its own Stiletto collection instance (infinite
        // scroll, Filter & Sort, quick view, ProductItem) before anyone
        // reacts to section:load.
        rebindTheme(oldGrid, newSection.querySelector(GRID_SEL));
        // Tell listeners a new section was inserted (BFCM hub nav/looks/gate,
        // and Stiletto itself inside the theme editor).
        document.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: SECTION_FETCH_ID, load: true }
        }));
        // Update dwell timer for next switch
        currentTabActivatedAt = Date.now();
      }

      // Pre-decode top images BEFORE the visible swap. This is what
      // kills the "screen goes white then updates" flicker — without
      // this, View Transitions snapshots the new section while its
      // images are still downloading, and the crossfade reveals empty
      // image placeholders. With this, the snapshot already has the
      // images rendered.
      //
      // Adds 100-400ms of "frozen old state" before swap (during which
      // user sees the old tab still active and old products still
      // visible). That's a much better UX than the white-flash gap.
      preloadAboveFoldImages(newSection).then(function () {
        if (mySeq !== swapSeq) return;
        if (typeof document.startViewTransition === 'function') {
          // Modern path — View Transitions API for native crossfade on
          // Chrome 111+ / Safari 18+. Respects prefers-reduced-motion
          // via CSS rules (no JS gating).
          //
          // Catch ALL VT promise rejections silently. The transition can
          // reject on `ready`, `updateCallbackDone`, or `finished` with
          // InvalidStateError when DOM mutations from Stiletto's section
          // reloader (fired via section:load inside performSwap) trip the
          // browser's transition-consistency check. The swap itself
          // completes correctly — only the animation is aborted — so we
          // need to silence all three rejection paths to keep the console
          // clean.
          try {
            var vt = document.startViewTransition(performSwap);
            if (vt) {
              if (vt.ready && typeof vt.ready.catch === 'function') vt.ready.catch(function () {});
              if (vt.updateCallbackDone && typeof vt.updateCallbackDone.catch === 'function') vt.updateCallbackDone.catch(function () {});
              if (vt.finished && typeof vt.finished.catch === 'function') vt.finished.catch(function () {});
            }
          } catch (e) {
            // Some browsers throw synchronously if a transition is in
            // flight — fall through to direct swap so the user still
            // gets the new tab.
            performSwap();
          }
        } else {
          // Fallback path for browsers without View Transitions
          // (Firefox, older Safari, etc.) — manual opacity crossfade
          // via the `data-collection-tabs-fading` attribute. Without
          // this fallback the swap is INSTANT and reads as a hard cut.
          //
          // Shorter fade (80ms each direction = 160ms total) now that
          // images are pre-loaded so the visible "blank" window is
          // minimised. Without the preload we'd need a longer fade to
          // hide the image-pop-in; with preload the swap is instant
          // and we just need to soften the visual cut.
          nav.setAttribute('data-collection-tabs-fading', 'true');
          requestAnimationFrame(function () {
            setTimeout(function () {
              performSwap();
              requestAnimationFrame(function () {
                nav.removeAttribute('data-collection-tabs-fading');
              });
            }, 80);
          });
        }
      });

      // Analytics
      track('collection_tab_switch', {
        from_tab_label: prevTabMeta ? prevTabMeta.label : null,
        from_tab_target: prevTabMeta ? prevTabMeta.target : null,
        to_tab_label: newTabMeta.label,
        to_tab_target: newTabMeta.target,
        to_tab_type: newTabMeta.type,
        swap_method: 'ajax',
        via: via,
        tab_dwell_ms: dwellMs,
        prefetched: wasPrefetched
      });
    });

    return true;
  }

  // ============================================================
  // Wire up — click + hover + touchstart on AJAX tabs.
  // ============================================================
  function bind() {
    nav.querySelectorAll('[data-tab-ajax="true"]').forEach(function (tab) {
      if (tab.dataset.tabsBound === '1') return;
      tab.dataset.tabsBound = '1';

      // Pre-fetch on hover (desktop)
      tab.addEventListener('mouseenter', function () {
        if (tab.classList.contains('collection-view-switcher__tab--active')) return;
        var href = tab.getAttribute('href');
        if (!href) return;
        if (!prefetchCache[href]) {
          track('collection_tab_prefetch', {
            tab_label: tabMeta(tab).label,
            tab_target: tabMeta(tab).target,
            via: 'hover'
          });
        }
        prefetch(href);
      }, { passive: true });

      // Pre-fetch on touchstart (mobile — fires ~100ms before click)
      tab.addEventListener('touchstart', function () {
        if (tab.classList.contains('collection-view-switcher__tab--active')) return;
        var href = tab.getAttribute('href');
        if (href) prefetch(href);
      }, { passive: true });

      // Click handler — intercept and swap
      tab.addEventListener('click', function (e) {
        // Let modifier-key clicks (cmd/ctrl/shift) go through to open in new tab
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        // Already active — no-op
        if (tab.classList.contains('collection-view-switcher__tab--active')) {
          e.preventDefault();
          return;
        }
        var href = tab.getAttribute('href');
        if (!href) return;
        e.preventDefault();
        var via = prefetchCache[href.split('#')[0]] ? 'hover_to_click' : 'click';
        doSwap(tab, href, via, false);
      });
    });
  }
  bind();

  // BFCM hub link (e.g. /collections/bfcm-2026) on a stage where it isn't a tab: the server already
  // marked the stage's first tab active and hid this page's own grid (html.bfcm-autotab); swap that
  // tab's grid in now (fromPop: no pushState; the address is replaced with the tab's URL once it's in).
  (function () {
    var hubEl = document.querySelector('[data-bfcm-autotab]');
    var first = hubEl && nav.querySelector('.collection-view-switcher__tab--active[data-tab-ajax="true"]');
    if (!first) { document.documentElement.classList.remove('bfcm-autotab'); return; }
    doSwap(first, hubEl.getAttribute('data-bfcm-autotab') || first.getAttribute('href'), 'auto', true);
  })();

  // Re-bind if Shopify reloads the section in Theme Editor (the editor
  // replaces the nav element, so look it up again first).
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-collection-tabs]')) {
      nav = document.querySelector('[data-collection-tabs]') || nav;
      bind();
    }
  });

  // ============================================================
  // Popstate — browser back/forward triggers a swap too.
  //
  // Match by current URL rather than relying on the state payload:
  // the FIRST history entry for the page wasn't pushed by us (it's
  // the natural initial load), so it has no state.collectionTabSwap
  // marker. If we keyed off state, going back to the original tab
  // would leave UI in a stale state (URL updates but active tab
  // doesn't swap). URL-based matching handles initial-load entries
  // and pushState entries uniformly.
  //
  // Guard: act only when the URL's collection differs from the grid
  // in the DOM (gridPath). Same-collection steps (#lookbook, Filter &
  // Sort entries) belong to their own handlers. The active tab is NOT
  // a reliable signal: the hub's lookbook script flips it on popstate
  // before this deferred listener runs.
  //
  // The popped URL is fetched as-is (filters in it are kept) and no
  // pushState happens: the browser has already moved the URL.
  // ============================================================
  window.addEventListener('popstate', function (e) {
    var currentPath = window.location.pathname;
    // Any swap still in flight is stale now (e.g. Back then Forward fast).
    swapSeq++;
    endSkeleton();
    if (currentPath === gridPath) return;
    var matchingTab = Array.prototype.find.call(
      nav.querySelectorAll('[data-tab-ajax="true"]'),
      function (t) { return pathOf(t.getAttribute('href')) === currentPath; }
    );
    if (!matchingTab) {
      // One of our own entries whose tab no longer exists: load it for real.
      if (e.state && e.state.collectionTabSwap) window.location.reload();
      return;
    }
    // Disambiguate back vs forward via state marker if present, else default to 'browser_back'
    var via = (e.state && e.state.collectionTabSwap === 'forward') ? 'browser_forward' : 'browser_back';
    doSwap(matchingTab, currentPath + window.location.search, via, true);
  });

  // ============================================================
  // Editorial-card injection — interleaves lifestyle imagery INTO
  // the product grid on low-density tabs (e.g. Couture). Only runs
  // when the active tab has data-tab-editorial="true". Reads
  // <template data-editorial-card> elements rendered by the section
  // and clones their content into the .collection__products grid
  // at calculated positions.
  //
  // Positioning:
  //   - Each card may have `data-after-product="N"` (1-indexed manual override)
  //   - Else uses section-level interval: (idx + 1) * interval
  //   - Inserts AFTER that product (e.g. interval=4, 1st card after 4th product)
  //
  // Run timing:
  //   - Initial page load (DOMContentLoaded or immediately if late)
  //   - shopify:section:load (fires after AJAX swap to a new tab)
  // ============================================================
  function injectEditorialCards() {
    // Only inject on tabs that opted in via low_density_layout
    if (nav.getAttribute('data-editorial') !== 'true') return;
    var templatesWrap = nav.querySelector('.collection-tabs-nav__editorial-templates');
    if (!templatesWrap) return;
    var templates = templatesWrap.querySelectorAll('template[data-editorial-card]');
    if (templates.length === 0) return;
    var grid = document.querySelector('.collection__products');
    if (!grid) return;

    // Idempotency: remove any previously-injected cards before re-injecting.
    // Necessary because shopify:section:load can fire multiple times during
    // a single swap, and Stiletto's section reloader can re-init in ways
    // that don't always replace the grid wholesale.
    grid.querySelectorAll('[data-injected-editorial="true"]').forEach(function (el) {
      el.remove();
    });

    var interval = parseInt(templatesWrap.getAttribute('data-editorial-interval'), 10) || 4;
    // Snapshot product children BEFORE injecting (so positions are stable).
    // Filter to direct element children that AREN'T already injected cards.
    var products = Array.prototype.filter.call(grid.children, function (c) {
      return c.getAttribute('data-injected-editorial') !== 'true';
    });

    templates.forEach(function (tpl, idx) {
      var manualPos = tpl.getAttribute('data-after-product');
      var positionAfter; // 1-indexed: insert AFTER the Nth product
      if (manualPos) {
        positionAfter = parseInt(manualPos, 10);
      } else {
        positionAfter = (idx + 1) * interval;
      }
      var targetProduct = products[positionAfter - 1]; // 0-indexed array
      if (!targetProduct) return; // Position beyond available products — skip silently

      // Clone the template's content (which is the editorial-card element).
      // `content` is a DocumentFragment; firstElementChild is our <a> or <div>.
      var clone = tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
      if (!clone) return;
      clone.setAttribute('data-injected-editorial', 'true');
      // Insert immediately after the target product (becomes the next sibling)
      targetProduct.insertAdjacentElement('afterend', clone);
    });
  }

  // ============================================================
  // Image-size correction for low-density tabs
  // ============================================================
  // Stiletto generates each product image's `src` and `sizes` based
  // on the section's `columns` setting (4 in our template). For the
  // default 4-col layout each card is ~25vw of the viewport, so
  // Stiletto serves images at ~400w (sufficient at 1x, just-enough
  // at 2x retina).
  //
  // When we CSS-override to 2-col (low-density), each card renders
  // at ~50vw on desktop / 100vw on mobile — but the image is still
  // the 400w variant Stiletto picked. The browser stretches it to
  // ~870px on desktop → 2.2x upscale → visible blur. Reported by
  // user: "Low density product images appear blurry."
  //
  // Fix: directly rewrite the `src` to bump the `?width=N` query
  // param to a size matching the actual rendered display width
  // (accounting for devicePixelRatio). Shopify's CDN serves any
  // requested width on demand from the same image record.
  //
  // We also update the `sizes` attribute as a hint to the browser
  // (helpful for srcset re-evaluation in cases where srcset already
  // has a large enough variant).
  //
  // Why not just upscale srcset? Stiletto's srcset DOES include
  // larger variants, but the browser already committed to the small
  // one before our JS ran. Re-evaluating srcset post-load is
  // unreliable across browsers. Direct src rewrite is the
  // dependable path.
  function pickShopifyWidthBucket(targetPx) {
    // Round to common widths to maximize CDN cache hit rate
    var buckets = [400, 600, 800, 1000, 1200, 1500, 1800, 2000];
    for (var i = 0; i < buckets.length; i++) {
      if (buckets[i] >= targetPx) return buckets[i];
    }
    return 2000;
  }
  function fixImageSizesForLowDensity() {
    if (nav.getAttribute('data-layout') !== 'low-density') return;
    var grid = document.querySelector('.collection__products');
    if (!grid) return;
    // Compute the target image width based on viewport + retina.
    // Low-density layout: 1col mobile (100vw), 2col desktop (50vw).
    var vw = window.innerWidth;
    var dpr = window.devicePixelRatio || 1;
    var cardCssWidth = vw < 750 ? vw : vw * 0.5;
    var targetPx = pickShopifyWidthBucket(Math.ceil(cardCssWidth * dpr));

    // Helper — bump the `?width=N` query param in a URL to targetPx,
    // and STRIP any `height` param.
    //
    // Why strip height: Stiletto's image URLs include both width AND
    // height params (e.g. `?width=400&height=600&crop=center`). Shopify
    // CDN treats these as a MAX-BOX constraint while preserving the
    // source image's aspect ratio — so if the source is 2:3 portrait
    // and the URL says `width=1500&height=147`, Shopify will serve
    // 98×147 (the largest 2:3 image that fits within 1500×147). Our
    // width upgrade gets entirely ignored because the small height
    // dominates.
    //
    // Stripping height tells Shopify "I only care about width, derive
    // height from the source aspect ratio." Combined with `crop=center`
    // (which we leave alone), this gives us a properly-sized image.
    function upgradeUrlWidth(url, w) {
      if (!url) return url;
      // Remove existing height param. Use [?&]height=\d+ pattern, then
      // clean up the resulting double-& or leading-? leftover.
      var clean = url
        .replace(/&height=\d+/g, '')
        .replace(/\?height=\d+&/g, '?')
        .replace(/\?height=\d+(?=$|#)/g, '');
      if (/[?&]width=\d+/.test(clean)) {
        return clean.replace(/([?&]width=)\d+/, '$1' + w);
      }
      return clean + (clean.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    // Stiletto wraps each product image in
    //   <picture><source type="image/webp" srcset="..."><img></picture>
    //
    // First-attempt approach (in-place srcset/src rewrite) didn't
    // actually trigger a re-fetch — browsers don't re-evaluate
    // <picture>/<source> selection after initial layout. Even after
    // updating the URLs, the browser kept rendering the
    // already-decoded small image. Verified live: img.currentSrc
    // reported the new URL but img.naturalWidth still reflected the
    // ORIGINAL response (668 vs the 1365 a fresh Image() probe got
    // from the exact same URL).
    //
    // Aggressive approach that actually works:
    //   1. Remove ALL <source> elements from the picture — kills
    //      Stiletto's WebP source variants. Browser falls back to
    //      <img src> entirely.
    //   2. Compute high-res URL (strip height, set big width).
    //   3. Force re-fetch by clearing img.src then setting the new
    //      one. The clear-then-set sequence is what triggers the
    //      browser to discard the old decoded bitmap and request the
    //      new URL fresh.
    //
    // Trade-off: we lose WebP on the low-density tab's initial render.
    // Acceptable — Couture is a small/curated subset, bandwidth diff
    // is modest, and sharpness is the priority. The standard tab
    // (RTW) still uses WebP since this function only runs on
    // low-density.
    var pictures = grid.querySelectorAll('picture');
    pictures.forEach(function (picture) {
      if (picture.closest('[data-injected-editorial="true"]')) return;

      var img = picture.querySelector('img');
      if (!img) return;
      // Idempotency — preserve originals once
      if (!img.hasAttribute('data-orig-sizes')) {
        img.setAttribute('data-orig-sizes', img.getAttribute('sizes') || '');
        img.setAttribute('data-orig-src', img.getAttribute('src') || '');
        img.setAttribute('data-orig-srcset', img.getAttribute('srcset') || '');
      }

      // Step 1: nuke all <source> elements so img.src wins
      picture.querySelectorAll('source').forEach(function (source) { source.remove(); });

      // Step 2: compute the new URL (strip height, bump width)
      var origSrc = img.getAttribute('data-orig-src') || img.getAttribute('src') || '';
      if (!origSrc) return;
      var newSrc = upgradeUrlWidth(origSrc, targetPx);

      // Step 3: force re-fetch via remove → set sequence
      img.removeAttribute('srcset');
      img.removeAttribute('src');
      img.setAttribute('sizes', '(max-width: 749px) 100vw, 50vw');
      // Setting src to the new URL triggers a fresh fetch.
      img.setAttribute('src', newSrc);
    });

    // Catch any imgs not wrapped in a <picture> (defensive)
    grid.querySelectorAll('img').forEach(function (img) {
      if (img.closest('picture')) return;
      if (img.closest('[data-injected-editorial="true"]')) return;
      var origSrc = img.getAttribute('data-orig-src') || img.getAttribute('src') || '';
      if (!origSrc) return;
      var newSrc = upgradeUrlWidth(origSrc, targetPx);
      img.removeAttribute('srcset');
      img.removeAttribute('src');
      img.setAttribute('sizes', '(max-width: 749px) 100vw, 50vw');
      img.setAttribute('src', newSrc);
    });
  }

  // Run injection + image-size correction on initial page load
  function onReady() {
    injectEditorialCards();
    fixImageSizesForLowDensity();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
  // Re-run after any AJAX swap (the grid is freshly replaced — our injected
  // cards are gone with the old DOM and need to be re-injected into the new one)
  document.addEventListener('shopify:section:load', function () {
    // setTimeout 0 — let Stiletto's section reloader finish its work first
    setTimeout(function () {
      injectEditorialCards();
      fixImageSizesForLowDensity();
    }, 0);
  });

  // ============================================================
  // Initial-load event — fire collection_tab_view so analytics can
  // measure "which tab did this session land on" (essential for the
  // ad-targeting hypothesis: cold RTW traffic shouldn't cross-pollute
  // into Couture at high rates).
  // ============================================================
  var activeTab = nav.querySelector('.collection-view-switcher__tab--active');
  if (activeTab) {
    var meta = tabMeta(activeTab);
    track('collection_tab_view', {
      tab_label: meta.label,
      tab_type: meta.type,
      tab_target: meta.target,
      collection_handle: (function () {
        // Best-effort extraction of the current collection handle from URL
        var match = window.location.pathname.match(/\/collections\/([^\/?]+)/);
        return match ? match[1] : null;
      })(),
      via: 'initial_load'
    });
  }
})();
